import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpcomingCalendar } from "@/features/meetings/hooks/use-upcoming-calendar";
import { notificationsApi } from "@/api/endpoints/notifications";
import { NOTIFICATION_KEYS } from "./use-notifications";

const CHECK_INTERVAL_MS = 30_000;   // re-scan the (cached) calendar every 30s
const LEAD_MS = 5 * 60_000;         // fire when a meeting starts within 5 minutes

/**
 * Upcoming-meeting reminder — fired 5 minutes before start. This is driven from
 * the frontend because the user's calendar is only fetched client-side
 * (`useUpcomingCalendar` → main Ubik backend); the workflows backend has no
 * server-side copy of future events. When a meeting enters the 5-min window we
 * persist a notification via the self-emit endpoint (so it lands in the bell and
 * the SSE stream plays the sound), deduped once per occurrence. The link is the
 * conference join URL when present, otherwise the meetings page.
 */
export function useMeetingReminders(userId: string | null | undefined): void {
  const { data } = useUpcomingCalendar({ enabled: !!userId });
  const queryClient = useQueryClient();
  const firedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userId) return;
    const events = data?.events ?? [];

    const scan = async () => {
      const now = Date.now();
      for (const ev of events) {
        if (!ev.start) continue;
        const startMs = new Date(ev.start).getTime();
        if (Number.isNaN(startMs)) continue;
        const delta = startMs - now;
        if (delta <= 0 || delta > LEAD_MS) continue; // not within the next 5 min
        const key = `meeting_reminder:${ev.id}:${ev.start}`;
        if (firedRef.current.has(key)) continue;
        firedRef.current.add(key);
        try {
          const mins = Math.max(1, Math.round(delta / 60_000));
          const { created } = await notificationsApi.selfEmit({
            type: "meeting_reminder",
            title: `Meeting in ${mins} min: ${ev.title || "Untitled"}`,
            body: ev.conference_url ? "Click to join the call." : undefined,
            link: ev.conference_url || "/meetings",
            tone: "attention",
            dedupKey: key,
            meta: { event_id: ev.id, start: ev.start, platform: ev.conference_platform },
          });
          if (created) queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
        } catch {
          firedRef.current.delete(key); // let the next tick retry on failure
        }
      }
    };

    void scan();
    const timer = setInterval(scan, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [userId, data, queryClient]);
}
